import React, { useState, useEffect } from 'react';
import Game from './Game';
import './App.css';
import io from 'socket.io-client';
import { Play, Users, ArrowLeft, BookOpen, Crown, Edit3, CheckSquare, Trophy, Shield, Info, Cookie } from 'lucide-react';
import AdBanner from './AdBanner';

const SOCKET_URL = 'https://api.eljuegodeldiccionario.es'; 
export const socket = io(SOCKET_URL);

const generateRoomId = () => Math.random().toString(36).substring(2, 6).toUpperCase();
// Generador del DNI único del jugador
const generateUserId = () => 'user_' + Math.random().toString(36).substring(2, 12);

function App() {

  // AHORA USAMOS LOCALSTORAGE PARA RESISTIR CIERRES COMPLETOS DE LA APP
  const [userId, setUserId] = useState(() => {
    let savedId = localStorage.getItem('dict_userId');
    if (!savedId) {
      savedId = generateUserId();
      localStorage.setItem('dict_userId', savedId);
    }
    return savedId;
  });

  const [username, setUsername] = useState(() => localStorage.getItem('dict_user') || '');
  const [roomId, setRoomId] = useState(() => localStorage.getItem('dict_room') || '');
  const [isPlaying, setIsPlaying] = useState(() => {
    return !!(localStorage.getItem('dict_user') && localStorage.getItem('dict_room'));
  });
  
  const [view, setView] = useState('menu'); // Puede ser: 'menu', 'join', 'legal', 'privacidad', 'cookies'
  const [errorMsg, setErrorMsg] = useState('');
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [view]);

  const handleCreateRoom = () => {
    if (!username) return alert("¡Ponte un nombre primero!");
    const newCode = generateRoomId();
    setRoomId(newCode);
    localStorage.setItem('dict_user', username);
    localStorage.setItem('dict_room', newCode);
    socket.emit('create-room', { roomId: newCode });
    setIsPlaying(true); 
  };

  const handleJoinRoom = () => {
    if (!username) return alert("¡Ponte un nombre primero!");
    if (!roomId) return alert("¡Introduce un código de sala!");
    socket.emit('check-room', roomId, (exists) => {
      if (exists) {
        localStorage.setItem('dict_user', username);
        localStorage.setItem('dict_room', roomId);
        setIsPlaying(true);
      } else {
        setErrorMsg("⚠️ ESA SALA NO EXISTE");
        setTimeout(() => setErrorMsg(''), 3000);
      }
    });
  };

  const handleRoomInput = (e) => setRoomId(e.target.value.toUpperCase());

  const handleGoBack = () => {
    // Al salir voluntariamente, borramos la sala y el nombre del jugador
    localStorage.removeItem('dict_user');
    localStorage.removeItem('dict_room');
    setIsPlaying(false);
    setView('menu');
  };

  if (isPlaying) {
    return <Game roomId={roomId} username={username} userId={userId} socket={socket} onGoBack={handleGoBack} />;
  }

  return (
    <div className="game-container">
      <div className="card">
        <h1 className="title-cool">EL JUEGO DEL<br/>DICCIONARIO</h1>
        <div className="subtitle">Engaña, Vota, Gana.</div>

        {/* --- MENÚ PRINCIPAL --- */}
        {view === 'menu' && (
          <div className="menu-buttons">
            <input 
              placeholder="¿CÓMO TE LLAMAS?" 
              onChange={(e) => setUsername(e.target.value)}
              value={username}
              maxLength={12}
            />
            <button className="btn-create" onClick={handleCreateRoom}>
              <Play size={20} className="icon-btn"/> CREAR SALA
            </button>
            <button className="btn-join" onClick={() => username ? setView('join') : alert('¡Ponte un nombre primero!')}>
              <Users size={20} className="icon-btn"/> UNIRSE A SALA
            </button>
            
            <button 
              style={{background: 'transparent', color: 'var(--text-dim)', border: '1px solid var(--secondary-muted)', marginTop: '10px'}} 
              onClick={() => setShowRules(!showRules)}
            >
              <BookOpen size={18} className="icon-btn" /> {showRules ? 'OCULTAR REGLAS' : 'CÓMO JUGAR'}
            </button>
          </div>
        )}

        {/* --- UNIRSE A SALA --- */}
        {view === 'join' && (
          <div style={{animation: 'fadeIn 0.3s'}}>
            <p style={{fontWeight: '600', color: 'var(--primary-dark)'}}>INTRODUCE EL CÓDIGO:</p>
            <input 
              placeholder="EJ: XJ9A" 
              onChange={handleRoomInput}
              value={roomId}
              style={{textAlign: 'center', fontSize: '1.5rem', letterSpacing: '2px'}}
              maxLength={4}
            />
            {errorMsg && <p style={{color: '#ef4444', fontWeight: 'bold'}}>{errorMsg}</p>}
            <button onClick={handleJoinRoom} className="btn-join" style={{background: 'var(--success-teal)', color: 'white', borderColor: 'var(--success-teal)'}}>
              ENTRAR A LA SALA
            </button>
            <button className="btn-back" onClick={() => setView('menu')} style={{width: '100%'}}>
              <ArrowLeft size={18} style={{verticalAlign: 'middle', marginRight: '5px'}}/> Volver al menú
            </button>
          </div>
        )}
      </div>

      {/* --- REGLAS --- */}
      {showRules && view === 'menu' && (
        <div className="card" style={{animation: 'fadeIn 0.4s', textAlign: 'left', padding: '30px'}}>
          <h3 style={{color: 'var(--primary-dark)', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px'}}>
            <BookOpen size={20} /> Cómo jugar
          </h3>
          <p style={{fontSize: '0.95rem', lineHeight: '1.6', color: 'var(--text-main)'}}>El objetivo es adivinar la definición real de las palabras y conseguir engañar a tus amigos con definiciones inventadas.</p>
          
          <h4 style={{color: 'var(--primary-dark)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            <Crown size={18} /> El Moderador
          </h4>
          <p style={{fontSize: '0.9rem', lineHeight: '1.5', marginTop: 0, color: 'var(--text-main)'}}>En cada ronda, el moderador elige una palabra del diccionario asegurándose de que nadie la conozca. Su único trabajo es elegir bien y observar el caos (no escribe ni vota).</p>
          
          <h4 style={{color: 'var(--primary-dark)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            <Edit3 size={18} /> Los Jugadores
          </h4>
          <p style={{fontSize: '0.9rem', lineHeight: '1.5', marginTop: 0, color: 'var(--text-main)'}}>El resto de jugadores verá la palabra elegida y tendrá que <strong>inventarse una definición falsa</strong> que suene muy convincente.</p>
          <div style={{background: 'var(--bg-light)', borderLeft: '4px solid var(--accent-blue)', padding: '10px 15px', marginTop: '10px', borderRadius: '0 8px 8px 0'}}>
            <p style={{fontSize: '0.85rem', margin: 0, color: 'var(--text-main)', lineHeight: '1.5'}}>
              <strong>⚠️ Importante:</strong> Escribe correctamente, ten cuidado con las faltas de ortografía, <strong>empieza con mayúscula y termina siempre con un punto</strong> para que no se diferencie de la definición real.
            </p>
          </div>
          
          <h4 style={{color: 'var(--primary-dark)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '20px'}}>
            <CheckSquare size={18} /> La Votación
          </h4>
          <p style={{fontSize: '0.9rem', lineHeight: '1.5', marginTop: 0, color: 'var(--text-main)'}}>Se mostrarán todas las definiciones inventadas mezcladas junto a la real. Deberás seleccionar la que creas que es verdadera.</p>
          
          <h4 style={{color: 'var(--primary-dark)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            <Trophy size={18} /> Puntuación y Desempate
          </h4>
          <ul style={{fontSize: '0.9rem', paddingLeft: '20px', color: 'var(--text-main)', lineHeight: '1.5', marginTop: 0}}>
            <li><strong>+2 puntos</strong> si votas la definición real del diccionario.</li>
            <li><strong>+1 punto</strong> por cada jugador que vote tu definición inventada.</li>
            <li style={{marginTop: '5px', color: 'var(--text-dim)'}}><em>Desempate:</em> Si hay empate, ganará el más rápido seleccionando las respuestas correctas.</li>
          </ul>

          <div style={{marginTop: '25px', paddingTop: '15px', borderTop: '1px solid var(--secondary-muted)', fontSize: '0.8rem', color: 'var(--text-dim)'}}>
            <p style={{margin: 0}}>Las definiciones de este juego han sido obtenidas de <strong>Wikcionario</strong> bajo licencia CC BY-SA 4.0.</p>
          </div>
        </div>
      )}

      {/* --- AVISO LEGAL --- */}
      {view === 'legal' && (
        <div className="card" style={{animation: 'fadeIn 0.3s', textAlign: 'left', padding: '30px'}}>
          <h3 style={{color: 'var(--primary-dark)', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px'}}><Info size={20}/> Aviso Legal</h3>
          <p style={{fontSize: '0.9rem'}}><strong>Dominio:</strong> eljuegodeldiccionario.es</p>
          <p style={{fontSize: '0.9rem', lineHeight: '1.5'}}><strong>Objeto:</strong> Este sitio web ofrece un juego recreativo basado en el uso de vocabulario. El acceso y uso del sitio atribuye la condición de usuario e implica la aceptación de los términos aquí expuestos.</p>
          <p style={{fontSize: '0.9rem', lineHeight: '1.5'}}><strong>Propiedad Intelectual:</strong> El código fuente y diseño son propiedad del titular. Las definiciones de palabras se obtienen de <strong>Wikcionario</strong> y están sujetas a la licencia <strong>CC BY-SA 4.0</strong>.</p>
          <button className="btn-back" onClick={() => setView('menu')} style={{width: '100%', marginTop: '20px'}}>
            <ArrowLeft size={18} style={{verticalAlign: 'middle', marginRight: '5px'}}/> Volver al inicio
          </button>
        </div>
      )}

      {/* --- POLÍTICA DE PRIVACIDAD --- */}
      {view === 'privacidad' && (
        <div className="card" style={{animation: 'fadeIn 0.3s', textAlign: 'left', padding: '30px'}}>
          <h3 style={{color: 'var(--primary-dark)', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px'}}><Shield size={20}/> Política de Privacidad</h3>
          <div style={{fontSize: '0.9rem', lineHeight: '1.5', color: 'var(--text-main)'}}>
            <p><strong>1. Datos que recopilamos:</strong><br/>
            - Datos de juego: Nombre de usuario (pseudónimo) para la sala de juego. No requerimos registro con nombre real ni apellidos.<br/>
            - Datos técnicos: Dirección IP y cookies técnicas para mantener la conexión con el servidor (WebSockets).</p>
            <p><strong>2. Finalidad:</strong><br/>
            Gestionar las partidas en tiempo real y, en caso de mostrar publicidad, personalizar los anuncios según las políticas de proveedores externos.</p>
            <p><strong>3. Base legal:</strong><br/>
            El consentimiento del usuario al acceder al juego y el interés legítimo para el funcionamiento técnico del servicio.</p>
            <p><strong>4. Terceros:</strong><br/>
            Utilizamos servicios de infraestructura externa: Vercel (Alojamiento), Render (Servidor) y proveedores de anuncios que pueden recopilar datos de navegación.</p>
          </div>
          <button className="btn-back" onClick={() => setView('menu')} style={{width: '100%', marginTop: '20px'}}>
            <ArrowLeft size={18} style={{verticalAlign: 'middle', marginRight: '5px'}}/> Volver al inicio
          </button>
        </div>
      )}

      {/* --- POLÍTICA DE COOKIES --- */}
      {view === 'cookies' && (
        <div className="card" style={{animation: 'fadeIn 0.3s', textAlign: 'left', padding: '30px'}}>
          <h3 style={{color: 'var(--primary-dark)', marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px'}}><Cookie size={20}/> Política de Cookies</h3>
          <div style={{fontSize: '0.9rem', lineHeight: '1.5', color: 'var(--text-main)'}}>
            <p>Este sitio utiliza cookies para mejorar la experiencia del usuario y mostrar publicidad.</p>
            <p><strong>1. ¿Qué son las cookies?</strong><br/>
            Son pequeños archivos que se almacenan en tu navegador para recordar información de tu sesión.</p>
            <p><strong>2. Tipos de cookies que usamos:</strong><br/>
            - <strong>Técnicas (Necesarias):</strong> Permiten que te mantengas en la sala de juego y que el sistema sepa quién es el moderador. Sin ellas, el juego no funciona.<br/>
            - <strong>Analíticas y de Publicidad:</strong> Si ves anuncios, nuestros socios publicitarios utilizan cookies para mostrarte publicidad relevante basada en tus visitas a este y otros sitios web.</p>
            <p><strong>3. Cómo desactivarlas:</strong><br/>
            Puedes configurar tu navegador para bloquear las cookies, pero es posible que el juego deje de funcionar correctamente.</p>
          </div>
          <button className="btn-back" onClick={() => setView('menu')} style={{width: '100%', marginTop: '20px'}}>
            <ArrowLeft size={18} style={{verticalAlign: 'middle', marginRight: '5px'}}/> Volver al inicio
          </button>
        </div>
      )}

      {/* --- FOOTER LEGAL --- */}
      {!isPlaying && (
        <footer style={{marginTop: '40px', paddingBottom: '20px', textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-dim)'}}>
          <div style={{display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '10px'}}>
            <span onClick={() => setView('privacidad')} style={{cursor: 'pointer', textDecoration: 'underline'}}>Privacidad</span>
            <span onClick={() => setView('cookies')} style={{cursor: 'pointer', textDecoration: 'underline'}}>Cookies</span>
            <span onClick={() => setView('legal')} style={{cursor: 'pointer', textDecoration: 'underline'}}>Aviso Legal</span>
          </div>
          <p>© 2026 eljuegodeldiccionario.es</p>
        </footer>
      )}
    </div>
  );
}

export default App;